'use client'

import React, { useCallback, useMemo } from 'react'
import { CollectionArchive } from '@/ui/components/CollectionArchive'
import { CardType } from '@/ui/components/Card/types'
import type { BaseGatedArchiveClientProps } from '../shared/gatedArchiveTypes'
import { FetchDataParams } from '@/lib/actions/fetchCollectionData'

// Special filter value for solutions
const SOLUTIONS_FILTER_VALUE = 'SOLUTIONS_FILTER'

/**
 * Builds a where clause for product filtering based on category and product type
 * @param baseWhere - Base where clause from gating/visibility logic
 * @param filterValue - Selected filter value (category ID, SOLUTIONS_FILTER, or null)
 * @param context - Context object containing contentType, categoryIds, and productGroupIdsByCategory
 */
const buildProductsFilterWhere = (
  baseWhere: any,
  filterValue: string | null,
  context?: { 
    contentType?: 'products' | 'solutions' | 'both'
    categoryIds?: string[]
    productGroupIdsByCategory?: Record<string, string[]>
  },
): any => {
  const contentType = context?.contentType || 'products'
  const categoryIds = context?.categoryIds || []
  const productGroupIdsByCategory = context?.productGroupIdsByCategory || {}

  // Get all product group IDs from all categories
  const allProductGroupIds = Object.values(productGroupIdsByCategory).flat()

  // No filter or "all" selected
  if (!filterValue || filterValue === 'all') {
    if (contentType === 'both' && allProductGroupIds.length > 0) {
      // Show all products from selected product groups + all solutions
      return {
        and: [
          baseWhere,
          {
            or: [
              { productType: { equals: 'solution' } },
              {
                and: [
                  { productType: { equals: 'product' } },
                  { 'productGroup.value': { in: allProductGroupIds } },
                ],
              },
            ],
          },
        ],
      }
    }
    // contentType already filters, use base where
    return baseWhere
  }

  // Solutions filter selected
  if (filterValue === SOLUTIONS_FILTER_VALUE) {
    return {
      and: [
        baseWhere,
        { productType: { equals: 'solution' } },
      ],
    }
  }

  // Specific category selected - get product group IDs for this category
  const productGroupIdsForCategory = productGroupIdsByCategory[filterValue] || []
  
  return {
    and: [
      baseWhere,
      { productType: { equals: 'product' } },
      { 'productGroup.value': { in: productGroupIdsForCategory } },
    ],
  }
}

/**
 * Props for ProductsArchiveClient component
 */
export interface ProductsArchiveClientProps extends BaseGatedArchiveClientProps {
  /** Card type to display (StandardFavorite or Teaser) */
  cardType?: CardType
  /** Content type for fetching correct data */
  contentType: 'products' | 'solutions' | 'both'
  /** Context data for custom filter builder */
  filterBuilderContext?: { 
    contentType: 'products' | 'solutions' | 'both'
    categoryIds: string[]
    productGroupIdsByCategory: Record<string, string[]>
  }
  /** Wiki page ID to exclude from "Used in" badges */
  excludeWikiId?: string
  /** Enable analytics tracking */
  enableTracking?: boolean
  /** Sorting options */
  sortingOptions?: any[]
}

/**
 * Client component for ProductsArchive
 *
 * Simplified client component that delegates all logic to CollectionArchive.
 * All gating, authentication, filtering, and rendering logic is now handled by the
 * generic CollectionArchive component.
 *
 * Creates wrapper functions client-side for contentType and custom filter builder.
 */
export const ProductsArchiveClient: React.FC<ProductsArchiveClientProps> = ({
  initialData,
  fetchCollectionDataAction,
  isGated,
  gateConfig,
  where,
  locale,
  limit,
  gridCols,
  filters = [],
  filterField,
  filterTitle,
  archiveId,
  cardType = CardType.StandardFavorite,
  contentType,
  excludeWikiId,
  filterBuilderContext,
  emptyStateType,
  emptyStateButton,
  emptyStateHasButton,
  enableTracking = true,
  sortingOptions,
}) => {
  // Create wrapper function client-side that includes contentType
  const fetchDataWithContentType = useCallback(
    async (params: FetchDataParams) => {
      return fetchCollectionDataAction({
        ...params,
        contentType,
        excludeWikiId,
      } as any)
    },
    [fetchCollectionDataAction, contentType, excludeWikiId]
  )

  // Create custom filter builder client-side (only if we have filters)
  const customFilterBuilder = useMemo(() => {
    if (filters.length === 0 || !filterBuilderContext) {
      return undefined
    }
    return buildProductsFilterWhere
  }, [filters.length, filterBuilderContext])

  return (
    <CollectionArchive
      archiveId={archiveId}
      initialData={initialData}
      fetchCollectionDataAction={fetchDataWithContentType}
      collection="products"
      locale={locale}
      where={where}
      limit={limit}
      gridCols={gridCols}
      filter={filters}
      filterField={filterField}
      filterTitle={filterTitle}
      cardType={cardType}
      loadMoreStrategy="pagination"
      sortingOptions={sortingOptions}
      // Gating props
      isGated={isGated}
      gateConfig={gateConfig}
      // Custom filter logic for category + solutions (defined client-side)
      customFilterBuilder={customFilterBuilder}
      filterBuilderContext={filterBuilderContext}
      // Empty state props
      emptyStateType={emptyStateType}
      emptyStateButton={emptyStateButton}
      emptyStateHasButton={emptyStateHasButton}
      // Tracking
      enableTracking={enableTracking}
    />
  )
}
